import React, { useMemo } from 'react';
import { Card, Box, Typography } from '@mui/material';
import { Bar } from 'react-chartjs-2';
import { BASE_OPTIONS, EJE_X_BASE, EJE_Y_BASE } from '../../Recepcion/reportes/chartBase';
import { COLOR, FUENTE, UMBRALES } from '../../Recepcion/reportes/tokens';

/**
 * Qué se presenta y cómo sale, por tipo de trámite.
 *
 * Barras apiladas y no una por tipo: lo que se compara no es solo el volumen sino la proporción
 * de cada desenlace dentro del tipo — dónde entra fácil y dónde rebota. Los colores tienen
 * significado y son los MISMOS que los de la barra de arriba (ResultadosDistribucion): azul lo
 * que entró y sigue en trámite, verde lo que salió bien, rojo lo que rebotó en la ventanilla,
 * rojo claro lo que ANSES denegó, gris lo que todavía no volvió.
 *
 * Favorable y desfavorable salieron del "Otros desenlaces" ámbar donde estaban: son los dos
 * finales posibles y pintarlos junto con los condicionales y los no presentados escondía
 * justamente lo que el estudio busca acá. En ámbar quedó solo lo que de verdad está en el medio.
 *
 * @param {{data: Array<{tipoTramite, cantidad, ingresadas, rechazadas, pendientes, favorables,
 *   desfavorables, tasaIngreso}>}} props
 */
const PresentacionesPorTipoChart = ({ data }) => {
    const filas = useMemo(() => (data || []).slice(0, 10), [data]);

    // Lo que queda en el medio: condicionales y no presentadas, que no tienen columna propia.
    const otros = useMemo(
        () => filas.map((f) => Math.max(0, f.cantidad - f.ingresadas - f.rechazadas - f.pendientes
            - (f.favorables || 0) - (f.desfavorables || 0))),
        [filas]);

    const chartData = {
        labels: filas.map((f) => (f.tipoTramite || 'Sin tipo').replaceAll('_', ' ')),
        datasets: [
            { label: 'Favorable', data: filas.map((f) => f.favorables || 0), backgroundColor: COLOR.verde, borderRadius: 4, barThickness: 20 },
            { label: 'En ANSES', data: filas.map((f) => f.ingresadas), backgroundColor: COLOR.azul, borderRadius: 4, barThickness: 20 },
            { label: 'Rechazadas', data: filas.map((f) => f.rechazadas), backgroundColor: COLOR.rojo, borderRadius: 4, barThickness: 20 },
            { label: 'Desfavorable', data: filas.map((f) => f.desfavorables || 0), backgroundColor: COLOR.rojoClaro, borderRadius: 4, barThickness: 20 },
            { label: 'Otros desenlaces', data: otros, backgroundColor: COLOR.ambar, borderRadius: 4, barThickness: 20 },
            { label: 'Sin resolver', data: filas.map((f) => f.pendientes), backgroundColor: COLOR.gris, borderRadius: 4, barThickness: 20 },
        ],
    };

    const options = {
        ...BASE_OPTIONS,
        indexAxis: 'y',
        scales: {
            x: { ...EJE_Y_BASE, stacked: true, beginAtZero: true, ticks: { ...EJE_Y_BASE.ticks, precision: 0 } },
            y: { ...EJE_X_BASE, stacked: true },
        },
        plugins: {
            ...BASE_OPTIONS.plugins,
            tooltip: {
                ...BASE_OPTIONS.plugins.tooltip,
                callbacks: {
                    afterBody: (items) => {
                        const fila = filas[items[0].dataIndex];
                        if (!fila) return '';
                        return fila.tasaIngreso === null || fila.tasaIngreso === undefined
                            // Se dice por qué no hay porcentaje, en vez de mostrar uno que engañe.
                            ? `Muestra chica (menos de ${UMBRALES.muestraMinimaAnses} resueltas): sin %`
                            : `Entraron: ${fila.tasaIngreso}% de las resueltas`;
                    },
                },
            },
        },
    };

    return (
        <Card variant="outlined" sx={{ border: '1px solid #e2e8f0', borderRadius: '12px', boxShadow: 'none', padding: '18px 20px' }}>
            <Typography sx={{ fontFamily: FUENTE, fontSize: 15, fontWeight: 700, color: COLOR.negro, marginBottom: '2px' }}>
                Qué se presenta y cómo sale
            </Typography>
            <Typography sx={{ fontFamily: FUENTE, fontSize: 12.5, color: COLOR.grisTexto, marginBottom: '14px' }}>
                Presentaciones por tipo de trámite. Una misma ida puede presentar más de una cosa.
            </Typography>
            {filas.length === 0 ? (
                <Typography sx={{ fontFamily: FUENTE, fontSize: 13, color: COLOR.grisFrio, padding: '32px 0', textAlign: 'center' }}>
                    No hay presentaciones en el período.
                </Typography>
            ) : (
                <Box sx={{ height: Math.max(220, filas.length * 46) }}>
                    <Bar data={chartData} options={options} />
                </Box>
            )}
        </Card>
    );
};

export default PresentacionesPorTipoChart;
